// What revoking every key of a PC ("Revoke all") does to Gyro on that PC, in the words the Support screen shows
// (PR #240 review). Kept free of React so the wording is unit tested.
//
// "Revoke all" sends the agent a signed device-revoked frame. Agents newer than 0.4.1.0 answer it by writing a
// quarantine marker in the signed-in user's profile (and, on a PC with the Gyro broker service, by disabling the
// machine policy too), then run disabled and headless on every later start. The agent never deletes the marker and
// re-authorizing the device does not lift it, so a re-admitted user stays cut off until IT does. The procedure is in
// desktop/intune/README-Intune.md ("Kill switch", step 2) and desktop/docs/PROVISIONING.md. "Revoke key" (one key)
// only revokes elevation and writes no marker.

/** The marker, in each affected user's profile (a packaged agent may hold its copy in the package's redirected AppData). */
export const QUARANTINE_MARKER_PATH = String.raw`%LocalAppData%\Exante360\Gyro\policy\quarantine.json`;

/** Where the Intune user-context script that deletes the marker is documented. */
export const QUARANTINE_SCRIPT_DOC = 'desktop/intune/README-Intune.md, "Kill switch" step 2';

/**
 * Exactly what IT must do on the PC to bring a quarantined agent back, besides authorizing its key again (the registry
 * re-enables a revoked key through a new authorization; the agent enrolls it on its next start, with retries).
 */
export const QUARANTINE_RESTORE_STEPS =
  `Delete ${QUARANTINE_MARKER_PATH} in each affected user's profile, including the packaged agent's redirected copy `
  + `(the Intune user-context script in ${QUARANTINE_SCRIPT_DOC}), then restart Gyro; or re-provision the PC. `
  + 'A PC with the Gyro broker service also needs its machine policy re-provisioned.';

/** The confirmation "Revoke all" asks for: what the revoke does on the PC and what restores it. */
export function revokeAllConfirmText(name: string): string {
  return `Revoke every key for ${name}?\n\n`
    + 'The trusted keys and custody history remain recorded, but the PC loses access immediately.\n\n'
    + 'Gyro on that PC also quarantines itself for every user whose key is revoked (agents newer than 0.4.1.0): it keeps '
    + 'running disabled, and re-authorizing the device does NOT bring it back on its own. To restore it, IT must '
    + `authorize its key again here and also: ${QUARANTINE_RESTORE_STEPS}`;
}

/**
 * The warning the authorize form shows while its device ID names a PC that already has a revoked key: re-admitting it
 * is not enough when the keys were revoked with "Revoke all". Null for a new device or one with no revoked key.
 */
export function readmitQuarantineNotice(
  devices: ReadonlyArray<{ deviceId: string; status: 'active' | 'revoked' }>,
  deviceId: string,
): string | null {
  const id = deviceId.trim();
  if (!id || !devices.some((device) => device.deviceId === id && device.status === 'revoked')) return null;
  return 'This PC has a revoked key. If it was revoked with "Revoke all", Gyro on it stays quarantined after this '
    + `authorization (agents newer than 0.4.1.0): ${QUARANTINE_RESTORE_STEPS}`;
}
